import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useRouteParams } from '@/context/routeParamsContext';
import { PageLevelNotification } from '@americanexpress/dls-react';
import { Link } from '@americanexpress/one-app-router';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { IconCheck } from '@americanexpress/dls-icons';
import {
  getMarketName,
  getCardArtUrl,
  getBasePathFromLocation,
  formatText,
  cn,
} from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { cardArtMapping } from '@/constants/productMapping';
import { locales } from '@/constants/supportedLocales';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { setSelectedProduct } from '@/slices/walletSlice';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import { getCardPathname } from '@/helpers/loungeHelper';
import { useLoungeById } from '@/hooks/loungesHooks/loungesHelperHooks';
import DividerWithMessage from './DividerWithMessage';
import styles from './PageLevelNotificationOverride.module.scss';

const Wallet = ({ wallet, handleClick }) => {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { selectCard, productDisplayNames } = useCommonContent();
  const dispatch = useDispatch();
  const { product, airportCode, loungeId } = useRouteParams();
  const lounge = useLoungeById(loungeId);

  const { locale } = useSelector(getLocaleData);
  const urlSpecifiedProductFromUsersWallet = useSelectedProductFromUserWallet(
    product,
    locale
  );
  const selectedCardLastDigits = urlSpecifiedProductFromUsersWallet
    ? urlSpecifiedProductFromUsersWallet.displayAccountNumber
    : '';
  const [, region = 'US'] = locale.split('-');
  const marketName = getMarketName(`en-${region}`); // force market name to be in English per LB-5054
  const formattedMarketName = ['US', 'UK'].includes(marketName)
    ? `the ${marketName}`
    : marketName;
  const activeProductDisplayName =
    productDisplayNames[product]?.selectCardDisplayName;
  const showMsgForProductNotInWallet =
    wallet?.find(
      (card) =>
        card.loungeFinderProduct === product &&
        card.homeCountryLocale?.toLocaleLowerCase() ===
          locale.toLocaleLowerCase()
    ) === undefined;

  let cardNotInWalletText = selectCard?.activeProductNotInWallet || '';
  // content will always be in English for the IDC cards
  cardNotInWalletText =
    locale === locales.UnitedStates && product?.includes('idc')
      ? cardNotInWalletText.replace(/ ?issued in {countryName} ?/g, '')
      : cardNotInWalletText;

  if (wallet.length === 0) {
    return (
      <>
        <PageLevelNotification
          isDismissible={false}
          type="caution"
          className={cn('tw-mb-6', styles.pageLevelNotification)}
        >
          <p className="-tw-ml-2 tw-pr-4 tw-text-base">
            {selectCard?.noLoungeBenefit}
          </p>
        </PageLevelNotification>
        <hr className="tw-mb-6 tw-border-gray-300" />
      </>
    );
  }

  return (
    <div className="tw-mb-6">
      {showMsgForProductNotInWallet && (
        <PageLevelNotification
          isDismissible={false}
          type="info"
          className={cn('tw-mb-6', styles.pageLevelNotification)}
        >
          <p
            className="-tw-ml-2 tw-pr-4 tw-text-base"
            dangerouslySetInnerHTML={{
              __html: formatText(cardNotInWalletText, {
                productTitle: activeProductDisplayName,
                countryName: formattedMarketName,
              }),
            }}
          />
        </PageLevelNotification>
      )}
      <p
        htmlFor="country-select"
        className="tw-mb-2 tw-inline tw-font-sans tw-text-base tw-font-bold tw-text-gray-700"
      >
        {selectCard?.selectLabel_loggedIn}
      </p>
      <div className="tw-mb-6 tw-flex tw-flex-col">
        {wallet.map((walletCardProduct) => {
          const walletCardProductlocale =
            walletCardProduct?.homeCountryLocale?.toLowerCase();
          const productName = walletCardProduct?.loungeFinderProduct;
          const lastDigits = walletCardProduct?.displayAccountNumber;
          const isSelectedCard =
            productName === product &&
            selectedCardLastDigits === lastDigits &&
            locale.toLowerCase() ===
              (walletCardProduct?.homeCountryLocale || '').toLowerCase();
          const imgSrc = getCardArtUrl({
            baseOneCmsImgUrl,
            product: cardArtMapping[productName],
            walletCardProductlocale,
          });
          const basePath = getBasePathFromLocation(
            walletCardProductlocale,
            window.location.pathname
          );
          return (
            <Link
              key={productName}
              to={{
                pathname: getCardPathname({
                  basePath,
                  product: productName,
                  airportCode,
                  lounge,
                  locale: walletCardProductlocale,
                }),
                search: window.location.search,
              }}
              className={cn(
                'tw-flex tw-items-center tw-gap-4 tw-px-2 tw-py-4 hover_tw-rounded-lg hover_tw-bg-gray-200',
                {
                  'tw-rounded-lg tw-bg-gray-200': isSelectedCard,
                }
              )}
              onClick={(e) => {
                dispatch(setSelectedProduct(walletCardProduct));
                handleClick(e, productName, walletCardProductlocale);
              }}
            >
              <img
                src={walletCardProduct?.cardArtUrl || imgSrc}
                alt="card-art"
                className="tw-h-11 tw-w-17dot5"
              />
              <div className="tw-flex tw-flex-col">
                <p className="tw-text-gray-800">
                  {walletCardProduct?.productName}
                </p>
                <p className="tw-text-sm tw-text-gray-600">
                  {formatText(selectCard?.accountNumber, { lastDigits })}
                </p>
              </div>
              {isSelectedCard && (
                <IconCheck
                  size="md"
                  isFilled={true}
                  className="tw-ml-auto tw-mr-2 tw-fill-gray-600"
                  data-testid="selected-card-check"
                />
              )}
            </Link>
          );
        })}
      </div>
      <DividerWithMessage message={selectCard?.divider} />
    </div>
  );
};

Wallet.propTypes = {
  wallet: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleClick: PropTypes.func.isRequired,
};

export default Wallet;
